/* Shared carousel: extracted from the existing commercial card carousel.
 * Adapters own content and layout; this controller owns every interaction.
 */
(() => {
  'use strict';
  if (window.MagmaCardCarousel) {
    window.MagmaCardCarousel.mount(document);
    if (typeof CustomEvent !== 'undefined') window.dispatchEvent(new CustomEvent('magma:carousel-ready'));
    return;
  }
  const instances = new Map();
  function createPagination(container, count) {
    container.replaceChildren();
    const dots = count > 1 ? Array.from({ length: count }, () => {
      const dot = document.createElement('span');
      dot.className = 'mg-carousel-dot';
      container.append(dot);
      return dot;
    }) : [];
    const select = value => {
      const indexes = Array.isArray(value) ? value : [value];
      container.classList.toggle('has-visible-range', indexes.length > 1);
      dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', indexes.includes(i));
        dot.classList.toggle('is-range-start', indexes.length > 1 && i === indexes[0]);
        dot.classList.toggle('is-range-end', indexes.length > 1 && i === indexes[indexes.length - 1]);
      });
    };
    select(0);
    return { dots, select };
  }
  function createSelectionRange(container, buttons) {
    const indicators = [];
    function select(indices = []) {
      const boundary = container.getBoundingClientRect();
      const rows = [];
      for (const index of indices) {
        const rect = buttons[index]?.getBoundingClientRect();
        if (!rect || !rect.width || !rect.height) continue;
        let row = rows.find(item => Math.abs(item.top - rect.top) < 2);
        if (!row) { row = {top: rect.top, left: rect.left, right: rect.right, height: rect.height}; rows.push(row); }
        else { row.left = Math.min(row.left, rect.left); row.right = Math.max(row.right, rect.right); row.height = Math.max(row.height, rect.height); }
      }
      const active = indices.length > 1 && rows.length > 0;
      container.classList.toggle('has-selection-range', active);
      rows.forEach((row, index) => {
        let indicator = indicators[index];
        if (!indicator) {
          indicator = document.createElement('span');
          indicator.className = 'mg-carousel-selection-range';
          indicator.setAttribute('aria-hidden', 'true');
          container.append(indicator);
          indicators.push(indicator);
        }
        indicator.hidden = !active;
        indicator.style.left = (row.left - boundary.left) + 'px';
        indicator.style.top = (row.top - boundary.top) + 'px';
        indicator.style.width = (row.right - row.left) + 'px';
        indicator.style.height = row.height + 'px';
      });
      indicators.forEach((indicator, index) => { if (!active || index >= rows.length) indicator.hidden = true; });
    }
    return {select, destroy() { indicators.forEach(indicator => indicator.remove()); container.classList.remove('has-selection-range'); }};
  }
  class CarouselController {
    constructor(root, options) {
      this.root = root;
      this.options = options;
      this.viewport = options.viewport;
      this.cards = [...options.cards];
      this.buttons = options.buttons || [];
      this.selected = 0;
      this.visible = [];
      this.highlighted = [];
      this.target = null;
      this.active = false;
      this.destroyed = false;
      this.drag = null;
      this.suppressClick = false;
      this.scrollTimer = 0;
      this.scrollFrame = 0;
      this.resizeFrame = 0;
      this.abort = new AbortController();
      this.mobile = window.matchMedia('(max-width: 767px)');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.buttons.forEach((button, index) => this.listen(button, 'click', () => this.go(index)));
      this.listen(this.viewport, 'scroll', () => this.onScroll(), { passive: true });
      this.listen(this.viewport, 'scrollend', () => this.finish());
      this.listen(this.viewport, 'pointerdown', event => this.startDrag(event), { passive: true });
      this.listen(this.viewport, 'pointermove', event => this.moveDrag(event));
      ['pointerup', 'pointercancel', 'lostpointercapture'].forEach(name => this.listen(this.viewport, name, event => this.endDrag(event)));
      this.listen(this.viewport, 'pointerleave', () => { if (this.drag && !this.drag.active) this.resetDrag(); });
      this.listen(this.viewport, 'selectstart', event => { if (this.drag) event.preventDefault(); });
      this.listen(window, 'blur', () => this.resetDrag());
      this.listen(this.viewport, 'dragstart', event => { if (this.canDrag()) event.preventDefault(); });
      this.listen(this.viewport, 'click', event => {
        if (this.suppressClick && event.detail !== 0) {
          event.preventDefault();
          event.stopPropagation();
          this.suppressClick = false;
        }
      }, { capture: true });
      this.listen(this.viewport, 'wheel', () => this.cancelNavigation(), { passive: true });
      this.listen(this.viewport, 'keydown', event => {
        if (!this.active || event.isComposing || event.altKey || event.ctrlKey || event.metaKey || event.target.closest('input,textarea,select,[contenteditable]')) return;
        if (event.target.closest('[data-magma-card-carousel]') !== this.root) return;
        const destinations = { ArrowLeft: this.selected - 1, ArrowRight: this.selected + 1, Home: 0, End: this.cards.length - 1 };
        if (Object.prototype.hasOwnProperty.call(destinations, event.key)) {
          event.preventDefault();
          this.go(destinations[event.key]);
        }
      });
      const resize = () => {
        cancelAnimationFrame(this.resizeFrame);
        this.resizeFrame = requestAnimationFrame(() => { this.resizeFrame = 0; this.refresh(); });
      };
      this.listen(window, 'resize', resize, { passive: true });
      this.listen(this.mobile, 'change', resize);
      this.listen(root, 'magma:layout-change', resize);
      if (typeof ResizeObserver !== 'undefined') {
        let width = this.viewport.clientWidth;
        this.observer = new ResizeObserver(() => {
          if (width !== this.viewport.clientWidth) { width = this.viewport.clientWidth; resize(); }
        });
        this.observer.observe(this.viewport);
      }
      this.refresh();
    }
    listen(element, name, callback, options = {}) {
      element?.addEventListener(name, callback, { ...options, signal: this.abort.signal });
    }
    bounded(index) { return Math.max(0, Math.min(index, this.cards.length - 1)); }
    itemsPerView() {
      const configured = typeof this.options.itemsPerView === 'function' ? this.options.itemsPerView(this) : this.options.itemsPerView;
      const value = Number(configured ?? 1);
      return Math.min(this.cards.length || 1, Math.max(1, Number.isFinite(value) ? Math.floor(value) : 1));
    }
    visibleIndices() {
      const viewport = this.viewport.getBoundingClientRect();
      const left = viewport.left;
      const right = left + this.viewport.clientWidth;
      return this.cards.flatMap((card, index) => {
        const rect = card.getBoundingClientRect();
        const overlap = Math.max(0, Math.min(right, rect.left + rect.width) - Math.max(left, rect.left));
        return rect.width > 0 && overlap / rect.width >= .75 ? [index] : [];
      });
    }
    refresh() {
      if (this.destroyed) return;
      this.resetDrag();
      this.cancelNavigation();
      this.options.layout?.(this);
      this.active = this.cards.length > 1 && (this.options.isActive?.(this) ?? true);
      if (this.active) this.go(this.selected, false);
      else this.select(this.selected, false);
      this.options.onScroll?.(this);
    }
    select(index, announce = false) {
      if (!this.cards.length || this.destroyed) return;
      this.selected = this.bounded(index);
      this.visible = this.itemsPerView() > 1 ? this.visibleIndices() : [this.selected];
      if (!this.visible.length) this.visible = [this.selected];
      if (this.itemsPerView() > 1) this.selected = this.visible[0];
      const mode = typeof this.options.selectionMode === 'function' ? this.options.selectionMode(this) : this.options.selectionMode;
      if (mode === 'first') this.highlighted = [this.selected];
      else if (mode === 'window' && this.itemsPerView() > 1) {
        const first = Math.min(this.selected, this.cards.length - this.itemsPerView());
        this.highlighted = Array.from({length: this.itemsPerView()}, (_, offset) => first + offset);
      } else this.highlighted = [...this.visible];
      this.buttons.forEach((button, i) => button.setAttribute('aria-pressed', String(this.highlighted.includes(i))));
      this.options.onSelect?.(this.selected, announce && this.active, this);
    }
    nearest() {
      if (this.itemsPerView() > 1) {
        const visible = this.visibleIndices();
        if (visible.length) return visible[0];
      }
      const rect = this.viewport.getBoundingClientRect();
      const center = rect.left + this.viewport.clientWidth / 2;
      let nearest = 0;
      let distance = Infinity;
      this.cards.forEach((card, index) => {
        const bounds = card.getBoundingClientRect();
        const delta = Math.abs(bounds.left + bounds.width / 2 - center);
        if (delta < distance) { nearest = index; distance = delta; }
      });
      return nearest;
    }
    go(index, smooth = true) {
      if (!this.active || this.destroyed || !this.cards.length) return;
      index = this.bounded(index);
      if (this.itemsPerView() > 1) index = Math.min(index, this.cards.length - this.itemsPerView());
      const bounds = this.viewport.getBoundingClientRect();
      const card = this.cards[index].getBoundingClientRect();
      const inset = this.itemsPerView() > 1 ? parseFloat(getComputedStyle(this.viewport).getPropertyValue('padding-left')) || 0 : (this.viewport.clientWidth - card.width) / 2;
      const left = this.viewport.scrollLeft + card.left - bounds.left - inset;
      this.scrollTo(left, smooth, index);
    }
    scrollTo(left, smooth = true, target = null) {
      if (!this.active || this.destroyed) return;
      this.target = target;
      clearTimeout(this.scrollTimer);
      this.scrollTimer = setTimeout(() => this.finish(), 250);
      this.viewport.scrollTo({ left: Math.max(0, Math.min(left, this.viewport.scrollWidth - this.viewport.clientWidth)), behavior: smooth && !this.reducedMotion.matches ? 'smooth' : 'instant' });
      if (target !== null) this.select(target);
    }
    page(direction) {
      if (!this.active || this.cards.length < 2) return;
      const first = this.cards[0].getBoundingClientRect();
      const second = this.cards[1].getBoundingClientRect();
      const step = Math.abs(second.left - first.left);
      if (!step) return;
      const gap = Math.max(0, step - first.width);
      const size = Math.max(1, Math.floor((this.viewport.clientWidth + gap) / step));
      this.scrollTo((Math.round(this.viewport.scrollLeft / step) + direction * size) * step);
    }
    cancelNavigation() {
      if (this.target !== null) this.viewport.scrollTo({ left: this.viewport.scrollLeft, behavior: 'instant' });
      this.target = null;
      clearTimeout(this.scrollTimer);
      this.scrollTimer = 0;
    }
    finish() {
      if (this.destroyed || this.drag?.active) return;
      clearTimeout(this.scrollTimer);
      this.scrollTimer = 0;
      this.target = null;
      if (this.active) this.select(this.nearest(), true);
      this.options.onScroll?.(this);
    }
    onScroll() {
      if (!this.active || this.destroyed) return;
      clearTimeout(this.scrollTimer);
      this.scrollTimer = setTimeout(() => this.finish(), 180);
      if (this.scrollFrame) return;
      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = 0;
        if (this.target === null || this.itemsPerView() > 1) this.select(this.nearest());
        this.options.onScroll?.(this);
      });
    }
    canDrag() { return this.active && (this.options.canDrag?.(this) ?? true); }
    startDrag(event) {
      this.resetDrag();
      this.cancelNavigation();
      this.suppressClick = false;
      // Touch is native browser scrolling, never a JavaScript touch gesture.
      if (!this.canDrag() || event.pointerType !== 'mouse' || event.button !== 0 || !event.isPrimary || event.target.closest('input,textarea,select,[contenteditable]')) return;
      this.drag = { id: event.pointerId, x: event.clientX, y: event.clientY, left: this.viewport.scrollLeft, index: this.nearest(), active: false };
      // Suppress mouse selection before the drag threshold, retaining normal
      // click/focus behaviour and leaving touch/keyboard selection untouched.
      this.viewport.classList.add('is-drag-pending');
    }
    moveDrag(event) {
      if (!this.drag || event.pointerId !== this.drag.id) return;
      const dx = event.clientX - this.drag.x;
      const dy = event.clientY - this.drag.y;
      if (!this.drag.active) {
        if (Math.abs(dy) > 8 && Math.abs(dy) >= Math.abs(dx)) { this.resetDrag(); return; }
        if (Math.abs(dx) < 8) return;
        this.drag.active = true;
        this.viewport.classList.add('is-dragging');
        this.viewport.setPointerCapture(event.pointerId);
      }
      event.preventDefault();
      this.viewport.scrollLeft = this.drag.left - dx;
    }
    resetDrag() {
      const gesture = this.drag;
      this.drag = null;
      this.viewport.classList.remove('is-drag-pending', 'is-dragging');
      if (gesture && this.viewport.hasPointerCapture(gesture.id)) this.viewport.releasePointerCapture(gesture.id);
    }
    endDrag(event) {
      if (!this.drag || event.pointerId !== this.drag.id) return;
      const gesture = this.drag;
      const delta = this.viewport.scrollLeft - gesture.left;
      let destination = this.nearest();
      if (destination === gesture.index && Math.abs(delta) > Math.min(60, this.viewport.clientWidth * .15)) destination += Math.sign(delta);
      this.resetDrag();
      if (gesture.active) { this.suppressClick = true; this.go(destination); }
    }
    destroy() {
      if (this.destroyed) return;
      this.resetDrag();
      this.cancelNavigation();
      this.destroyed = true;
      this.abort.abort();
      this.observer?.disconnect();
      cancelAnimationFrame(this.scrollFrame);
      cancelAnimationFrame(this.resizeFrame);
    }
  }

  // Existing commercial markup adapter. Styling/content remain in the section.
  class CardCarousel {
    constructor(root) {
      this.root = root;
      this.grid = root.querySelector(':scope > .mg-carousel-track');
      this.cards = [...this.grid.children].filter(card => card.matches('[data-mg-card]'));
      this.selector = root.querySelector(':scope > .mg-carousel-selector');
      this.selector.replaceChildren();
      this.buttons = this.cards.map((card, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.textContent = card.dataset.carouselLabel?.trim() || card.querySelector('h2,h3,h4,h5,h6')?.textContent.trim() || String(index + 1);
        const artwork = card.querySelector('.mg-image-mask, .mg-image svg, .mg-image img');
        if (artwork && root.dataset.selectorIcons !== 'false') {
          const icon = artwork.cloneNode(true);
          icon.classList.add('mg-selector-icon');
          ['width', 'height', 'color'].forEach(property => icon.style.removeProperty(property));
          ['id', 'alt', 'role', 'aria-label'].forEach(name => icon.removeAttribute(name));
          icon.setAttribute('aria-hidden', 'true');
          button.prepend(icon);
        }
        button.setAttribute('aria-controls', this.grid.id);
        this.selector.append(button);
        return button;
      });
      this.controls = root.querySelector(':scope > .mg-carousel-controls');
      this.previous = this.controls.querySelector(':scope > .mg-carousel-previous');
      this.next = this.controls.querySelector(':scope > .mg-carousel-next');
      this.pagination = this.controls.querySelector(':scope > .mg-carousel-dots');
      this.status = root.querySelector(':scope > .mg-carousel-status');
      this.multiple = this.cards.length > 1;
      root.classList.toggle('mg-carousel--single', !this.multiple);
      this.paginationView = createPagination(this.pagination, this.cards.length);
      this.dots = this.paginationView.dots;
      this.controller = new CarouselController(root, {
        viewport: this.grid, cards: this.cards, buttons: this.buttons,
        isActive: controller => controller.mobile.matches && root.dataset.mobileEnabled !== 'false',
        layout: controller => this.layout(controller),
        onSelect: (index, announce) => {
          this.paginationView.select(index);
          this.previous.hidden = !this.multiple || index === 0;
          this.next.hidden = !this.multiple || index === this.cards.length - 1;
          if (announce) this.status.textContent = (root.dataset.statusFormat || '{current} / {total}').replace('{current}', String(index + 1)).replace('{total}', String(this.cards.length)).replace('{label}', this.buttons[index].textContent);
        }
      });
      this.controller.listen(this.previous, 'click', () => this.controller.go(this.controller.selected - 1));
      this.controller.listen(this.next, 'click', () => this.controller.go(this.controller.selected + 1));
    }
    layout(controller) {
      this.cards.forEach(card => { card.style.gridColumn = ''; });
      this.grid.style.gridTemplateColumns = '';
      const active = controller.mobile.matches && this.multiple && this.root.dataset.mobileEnabled !== 'false';
      this.root.classList.toggle('mg-carousel--ready', active);
      this.selector.hidden = !active;
      this.controls.hidden = !active;
      if (active) {
        this.grid.tabIndex = 0;
        this.grid.setAttribute('role', 'region');
        this.grid.setAttribute('aria-roledescription', 'carousel');
      } else {
        ['tabindex', 'role', 'aria-roledescription'].forEach(name => this.grid.removeAttribute(name));
        this.grid.scrollLeft = 0;
        if (!controller.mobile.matches && this.root.dataset.lastRow === 'center') {
          const styles = getComputedStyle(this.root);
          const configured = parseInt(styles.getPropertyValue('--mg-columns'), 10) || 3;
          const columns = parseInt(styles.getPropertyValue('--mg-collection-effective-columns'), 10) || (window.innerWidth <= 1023 ? parseInt(styles.getPropertyValue('--mg-tablet-columns'), 10) || configured : configured);
          this.grid.style.gridTemplateColumns = `repeat(${columns * 2}, minmax(0, 1fr))`;
          this.cards.forEach(card => { card.style.gridColumn = 'span 2'; });
          const remainder = this.cards.length % columns;
          if (remainder) this.cards[this.cards.length - remainder].style.gridColumn = `${columns - remainder + 1} / span 2`;
        }
      }
    }
    selectBlock(blockId, target) {
      const index = this.cards.findIndex(card => card.dataset.mgCard === blockId || card.contains(target));
      if (index >= 0) this.controller.go(index, false);
    }
    destroy() {
      this.controller.destroy();
      this.root.classList.remove('mg-carousel--ready');
      this.selector.hidden = true;
      this.controls.hidden = true;
      this.pagination.replaceChildren();
    }
  }
  function mount(container = document) {
    const roots = [...container.querySelectorAll('[data-magma-card-carousel]')];
    if (container.matches?.('[data-magma-card-carousel]')) roots.unshift(container);
    roots.forEach(root => {
      if (!instances.has(root) && root.querySelector(':scope > .mg-carousel-track') && root.querySelector(':scope > .mg-carousel-selector') && root.querySelector(':scope > .mg-carousel-controls')) instances.set(root, new CardCarousel(root));
    });
  }
  function destroy(container = document) {
    for (const [root, instance] of instances) {
      if (container === root || container.contains(root)) { instance.destroy(); instances.delete(root); }
    }
  }
  document.addEventListener('shopify:section:load', event => mount(event.target));
  document.addEventListener('shopify:section:unload', event => {
    destroy(event.target);
  });
  document.addEventListener('shopify:block:select', event => {
    const root = event.target.closest('[data-magma-card-carousel]');
    instances.get(root)?.selectBlock(event.detail?.blockId, event.target);
  });
  window.MagmaCardCarousel = { mount, destroy, createPagination, createSelectionRange, create: (root, options) => new CarouselController(root, options) };
  if (typeof CustomEvent !== 'undefined') window.dispatchEvent(new CustomEvent('magma:carousel-ready'));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mount(document), { once: true });
  else mount(document);
})();
